import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, OnDestroy, ViewChild, afterEveryRender, computed, effect, inject, input, signal, untracked } from '@angular/core';
import { DatePipe, DecimalPipe } from '@angular/common';
import { RouterLink } from '@angular/router';
import { HlmButtonDirective } from '@spartan-ng/helm/button';
import { HlmInputDirective } from '@spartan-ng/helm/input';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideSearch, lucidePlus, lucideMinus, lucideLocateFixed, lucideX, lucideArrowUpRight } from '@ng-icons/lucide';
import { AnalysisService } from '@shared/services/analysis.service';
import { AddressService } from '@shared/services/address.service';
import { AnalysisPlanning } from '@shared/models/analysis.model';
import * as L from 'leaflet';
import { MapEntry, initialViewportPoints, organizationMapEntries, addressKey, Building, BuildingCell, CAPACITY_COLORS, analysesInBuilding, buildingCells, capacityColor, UNKNOWN_COLOR, canLoadPlanning, location, parseBuildings, primaryPlanning } from './solar-map-data';

const ROOF_ZOOM = 16;
/** Leaflet's zoom transition takes 250 ms, plus a little for its canvas paint. */
const LEAFLET_ZOOM_END_MS = 300;

/** The colour of one analysis, or null to leave it in the background. */
export type MapPaint = (row: MapEntry) => string | null;

type Result = { plan: AnalysisPlanning | null; error?: boolean };

@Component({
  host: { '[class.map-surface]': 'surface()' },
  selector: 'app-solar-map',
  standalone: true,
  imports: [DatePipe, DecimalPipe, RouterLink, NgIcon, HlmButtonDirective, HlmInputDirective],
  providers: [provideIcons({ lucideSearch, lucidePlus, lucideMinus, lucideLocateFixed, lucideX, lucideArrowUpRight })],
  templateUrl: './solar-map.html',
  styleUrl: './solar-map.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SolarMap implements AfterViewInit, OnDestroy {
  readonly organizationId = input.required<string>();
  readonly organizationSlug = input.required<string>();
  readonly campaignId = input<string>();
  /** Work surface: the map fills its box, without its heading and note. */
  readonly surface = input(false);
  /** Colours come from the page instead of one request per planning; it also shows the legend then. */
  readonly paint = input<MapPaint | null>(null);
  readonly returnTo = computed(() => this.campaignId()
    ? `/organizations/${this.organizationSlug()}/campaigns/${this.campaignId()}`
    : `/organizations/${this.organizationSlug()}/dashboard`);
  private readonly api = inject(AnalysisService);
  private readonly addresses = inject(AddressService);
  @ViewChild('mapCanvas', { static: true }) private canvas!: ElementRef<HTMLDivElement>;
  @ViewChild('mapPreview', { static: true }) private preview?: ElementRef<HTMLElement>;
  private map?: L.Map;
  private popup?: L.Popup;
  private focusPreview = false;
  private layers = L.layerGroup();
  private rendered = new Map<string, L.Path>();
  private observer?: ResizeObserver;
  private generation = 0;
  private viewportVersion = 0;
  private timer?: ReturnType<typeof setTimeout>;
  private controller?: AbortController;
  private buildings = new Map<number, Building>();
  private cells: BuildingCell[] = [];
  private loadedCells = new Set<string>();
  private failedCells = new Set<string>();
  private footprintRun?: Promise<void>;
  private buildingAnalyses = new Map<number, MapEntry[]>();
  private pending = new Map<string, Promise<void>>();
  private resultCache: Record<string, Result> = {};
  private resultRun?: Promise<void>;
  readonly resultsLoading = signal(false);
  readonly resultProgress = signal(0);
  readonly resultTotal = computed(() => this.located().filter(canLoadPlanning).length);
  readonly tilesLoading = signal(false);
  readonly mapBusy = computed(() => this.resultsLoading() || this.resultRequests() > 0 || this.detailLoading() || this.tilesLoading());
  readonly zoomLevel = signal(6);
  readonly rows = signal<MapEntry[]>([]);
  readonly loading = signal(true);
  readonly error = signal(false);
  readonly mapError = signal(false);
  readonly footprintError = signal(false);
  readonly detailLoading = signal(false);
  readonly resultRequests = signal(0);
  readonly resultCount = computed(() => Object.values(this.results()).filter(result => result.plan).length);
  readonly selectedRoofResult = computed(() => this.roofResult(this.selectedBuildingRows()));
  readonly selectedBuildingRows = signal<MapEntry[]>([]);
  readonly query = signal('');
  readonly selected = signal<MapEntry | null>(null);
  readonly results = signal<Record<string, Result>>({});
  readonly colors = CAPACITY_COLORS;
  readonly canLoadPlanning = canLoadPlanning;
  readonly located = computed(() => this.rows().filter(row => location(row)));
  readonly addressCount = computed(() => new Set(this.located().map(addressKey)).size);
  readonly missing = computed(() => new Set(this.rows().filter(row => !location(row)).map(addressKey)).size);
  readonly addressGroups = computed(() => {
    const groups = new Map<string, MapEntry[]>();
    for (const row of this.located()) {
      const key = addressKey(row);
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(row);
    }
    return groups;
  });
  readonly previewAnalyses = computed(() => {
    const rows = this.selectedBuildingRows().length ? this.selectedBuildingRows() : this.selected() ? [this.selected()!] : [];
    return rows.filter(row => !row.addressOnly).sort((a, b) =>
      new Date(b.createdAt ?? 0).getTime() - new Date(a.createdAt ?? 0).getTime() || a.analysisId.localeCompare(b.analysisId));
  });
  readonly matches = computed(() => {
    const query = this.query().trim().toLocaleLowerCase('de');
    return query ? [...this.addressGroups().values()].map(rows => rows[0]).filter(row => this.label(row).toLocaleLowerCase('de').includes(query)).slice(0, 8) : [];
  });
  readonly selectionResult = computed(() => {
    const row = this.selected();
    // Preview can finish independently while map colors wait for the full batch.
    this.resultProgress();
    return row ? this.resultCache[row.analysisId] ?? this.results()[row.analysisId] : undefined;
  });
  readonly selectionHasFootprint = signal(false);
  readonly resultError = computed(() => Object.values(this.results()).some(result => result.error));

  constructor() {
    effect(() => { void this.load(this.organizationId(), this.campaignId()); });
    effect(() => { if (this.paint()) untracked(() => this.draw()); });
    afterEveryRender(() => {
      if (!this.popup?.isOpen()) return;
      this.popup.update();
      if (this.focusPreview) {
        this.preview?.nativeElement.focus({ preventScroll: true });
        this.focusPreview = false;
      }
    });
  }

  ngAfterViewInit(): void {
    this.map = L.map(this.canvas.nativeElement, { zoomControl: false, scrollWheelZoom: true, preferCanvas: true, maxZoom: 20 }).setView([51, 10], 6);
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxNativeZoom: 19, maxZoom: 20,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    }).on('loading', () => this.tilesLoading.set(true))
      .on('load', () => this.tilesLoading.set(false))
      .on('tileerror', () => this.mapError.set(true)).addTo(this.map);
    this.map.on('popupclose', () => {
      this.selected.set(null); this.selectedBuildingRows.set([]); this.draw();
    });
    this.layers.addTo(this.map);
    // The page may lay its charts over the right side; the credit has to stay readable.
    if (this.paint()) this.map.attributionControl?.setPosition('bottomleft');
    this.map.on('moveend', () => {
      if (!this.map) return;
      this.zoomLevel.set(this.map.getZoom()); this.draw(); this.scheduleViewport();
    });
    this.observer = new ResizeObserver(() => this.map?.invalidateSize());
    this.observer.observe(this.canvas.nativeElement);
    if (this.located().length) this.fitArea(true);
  }

  async load(org = this.organizationId(), campaign = this.campaignId()): Promise<void> {
    const generation = ++this.generation;
    this.controller?.abort();
    this.viewportVersion++;
    this.popup?.remove(); this.rows.set([]); this.results.set({}); this.pending.clear(); this.resultRequests.set(0); this.selected.set(null);
    this.resultCache = {}; this.resultProgress.set(0); this.resultRun = undefined; this.resultsLoading.set(false);
    this.buildings.clear(); this.buildingAnalyses.clear(); this.cells = []; this.loadedCells.clear(); this.failedCells.clear(); this.footprintRun = undefined; this.layers.clearLayers(); this.rendered.clear();
    this.selectedBuildingRows.set([]);
    this.loading.set(true); this.error.set(false); this.query.set('');
    this.footprintError.set(false); this.detailLoading.set(false); 
    try {
      let rows: MapEntry[];
      if (campaign) {
        // Warm the SI address cache before reading campaign coordinates.
        await this.addresses.getPaginatedAddresses(org, 1, 1).catch(() => undefined);
        if (generation !== this.generation) return;
        rows = (await this.api.getAllAnalysesByCampaign(org, campaign)).analyses;
      } else {
        const addresses = [];
        let page = 1, pages = 1;
        do {
          const response = await this.addresses.getPaginatedAddresses(org, page, 1000);
          if (generation !== this.generation) return;
          addresses.push(...response.data);
          pages = response.meta.pages;
          page++;
        } while (page <= pages);
        const analyses = await this.api.getAnalysesByOrganization(org);
        rows = organizationMapEntries(addresses, analyses);
      }
      if (generation !== this.generation) return;
      this.rows.set(rows);
      this.resultsLoading.set(!this.paint() && this.located().some(canLoadPlanning));
      this.cells = buildingCells(rows);
      this.fitArea(true);
      void this.loadResults(generation);
    } catch {
      if (generation === this.generation) this.error.set(true);
    } finally {
      if (generation === this.generation) this.loading.set(false);
    }
  }

  label(row: MapEntry): string {
    const address = row.address;
    return address ? `${address.street ?? ''} ${address.houseNumber ?? ''}, ${address.postalCode ?? ''} ${address.city ?? ''}`.trim() : row.title || 'Adresse';
  }

  fitArea(initial = false): void {
    const allPoints = [...this.addressGroups().values()].map(rows => location(rows[0])!);
    const points = initial ? initialViewportPoints(allPoints) : allPoints;
    if (this.map && points.length) {
      this.map.fitBounds(L.latLngBounds(points), { padding: [32, 32], maxZoom: 18, animate: false });
      this.scheduleViewport();
    }
  }

  zoom(delta: number): void { this.map?.setZoom(this.map.getZoom() + delta); }

  open(row: MapEntry, footprint = false, buildingRows: MapEntry[] = [], anchor?: L.LatLngExpression): void {
    this.selectedBuildingRows.set(buildingRows.length ? buildingRows : this.addressGroups().get(addressKey(row)) ?? [row]);
    this.selected.set(row);
    this.selectionHasFootprint.set(footprint);
    const point = anchor ?? location(row);
    if (point && this.map && this.preview) {
      this.popup ??= L.popup({
        className: 'campaign-map-popup', closeButton: false,
        minWidth: 240, maxWidth: 288, offset: [0, -8],
        autoPanPaddingTopLeft: [12, 56], autoPanPaddingBottomRight: [64, 32],
      });
      this.popup.setLatLng(point).setContent(this.preview.nativeElement).openOn(this.map);
    }
    if (canLoadPlanning(row) && !this.results()[row.analysisId]) void this.loadResult(row, this.generation);
    this.draw();
  }

  locate(row: MapEntry): void {
    const point = location(row);
    if (point) this.map?.setView(point, 19);
    this.query.set('');
    const occupants = [...this.buildingAnalyses.values()].find(rows => rows.some(item => item.analysisId === row.analysisId));
    this.focusPreview = true;
    this.open(row, !!occupants, occupants);
  }

  close(): void {
    const restoreFocus = this.preview?.nativeElement.contains(document.activeElement);
    this.popup?.remove(); this.selected.set(null); this.selectedBuildingRows.set([]); this.draw();
    if (restoreFocus) this.canvas.nativeElement.focus({ preventScroll: true });
  }

  selectBuildingAnalysis(id: string): void {
    const rows = this.selectedBuildingRows();
    const row = rows.find(item => item.analysisId === id);
    if (row) this.open(row, true, rows, this.popup?.getLatLng());
  }

  resultFor(row: MapEntry): Result | undefined {
    this.resultProgress();
    return this.resultCache[row.analysisId] ?? this.results()[row.analysisId];
  }

  previewStatus(row: MapEntry): string {
    if (this.resultFor(row)?.error) return 'Ergebnis nicht verfügbar';
    if (row.technicalState === 'failed' || row.technicalStatus?.state === 'failed') return 'Fehlgeschlagen';
    if (canLoadPlanning(row) && !this.resultFor(row)) return 'Ergebnis lädt …';
    return 'Noch kein Ergebnis';
  }

  retryFootprints(): void {
    this.failedCells.clear();
    this.footprintError.set(false);
    this.scheduleViewport();
  }

  retryResults(): void {
    this.resultCache = Object.fromEntries(Object.entries(this.resultCache).filter(([, result]) => !result.error));
    this.results.update(results => Object.fromEntries(Object.entries(results).filter(([, result]) => !result.error)));
    this.resultProgress.set(Object.keys(this.resultCache).length);
    const row = this.selected();
    if (row) void this.loadResult(row, this.generation);
    this.scheduleViewport();
  }

  private scheduleViewport(): void {
    clearTimeout(this.timer);
    const version = ++this.viewportVersion;
    this.timer = setTimeout(() => { void this.loadViewport(version); }, 600);
  }

  private async loadViewport(version: number): Promise<void> {
    const map = this.map;
    if (!map || version !== this.viewportVersion || !this.located().length) return;
    this.draw();
    const generation = this.generation;
    const results = this.loadResults(generation);
    if (map.getZoom() >= ROOF_ZOOM && !this.footprintRun) {
      const run = this.loadFootprints(generation);
      this.footprintRun = run;
      void run.finally(() => { if (generation === this.generation) this.footprintRun = undefined; });
    }
    await results;
  }

  private loadResults(generation: number): Promise<void> {
    if (this.paint()) return Promise.resolve();
    if (this.resultRun) return this.resultRun;
    const queue = this.located().filter(row => canLoadPlanning(row) && !this.resultCache[row.analysisId]);
    if (!queue.length) {
      this.resultsLoading.set(false);
      this.results.set({ ...this.resultCache });
      this.draw();
      return Promise.resolve();
    }
    this.resultsLoading.set(true);
    // Finish one map-wide batch even while panning; publish all colors together.
    const workers = Array.from({ length: 3 }, async () => {
      while (queue.length && generation === this.generation) await this.loadResult(queue.shift()!, generation);
    });
    this.resultRun = Promise.all(workers).then(() => {
      if (generation !== this.generation) return;
      this.resultsLoading.set(false);
      this.resultRun = undefined;
      this.results.set({ ...this.resultCache });
      this.draw();
    });
    return this.resultRun;
  }

  private async loadFootprints(generation: number): Promise<void> {
    if (!this.map || this.map.getZoom() < ROOF_ZOOM) return;
    this.detailLoading.set(true);
    // One request at a time, retaining all loaded outlines when the viewport changes.
    try {
      while (generation === this.generation && this.map && this.map.getZoom() >= ROOF_ZOOM) {
        const bounds = this.map.getBounds();
        const center = bounds.getCenter();
        const cell = this.cells.filter(item => !this.loadedCells.has(item.key) && !this.failedCells.has(item.key) &&
          bounds.intersects([[item.south, item.west], [item.north, item.east]]))
          .sort((a, b) => L.latLng((a.south + a.north) / 2, (a.west + a.east) / 2).distanceTo(center) -
            L.latLng((b.south + b.north) / 2, (b.west + b.east) / 2).distanceTo(center))[0];
        if (!cell) break;
        const controller = new AbortController();
        this.controller = controller;
        const timeout = setTimeout(() => controller.abort(), 15000);
        try {
          // Public grid bounds only; never send customer addresses or credentials.
          const bbox = `${cell.west},${cell.south},${cell.east},${cell.north}`;
          // shortcut: small public extracts; use a hosted vector source before high-volume rollout.
          const response = await fetch(`https://api.openstreetmap.org/api/0.6/map.json?bbox=${bbox}`, {
            credentials: 'omit', signal: controller.signal,
          });
          if (!response.ok) throw new Error('Building service unavailable');
          const buildings = parseBuildings(await response.json());
          if (generation !== this.generation) return;
          for (const building of buildings) {
            if (this.buildings.has(building.id)) continue;
            const occupants = analysesInBuilding(building, this.cells);
            if (!occupants.length) continue;
            this.buildings.set(building.id, building);
            this.buildingAnalyses.set(building.id, occupants);
          }
          this.loadedCells.add(cell.key);
          this.draw();
          const selected = this.selected();
          if (selected) {
            const occupants = [...this.buildingAnalyses.values()].find(rows => rows.some(row => row.analysisId === selected.analysisId));
            this.selectionHasFootprint.set(!!occupants);
            this.selectedBuildingRows.set(occupants ?? this.addressGroups().get(addressKey(selected)) ?? [selected]);
          }
        } catch {
          if (generation === this.generation) { this.failedCells.add(cell.key); this.footprintError.set(true); }
        } finally { clearTimeout(timeout); }
      }
    } finally { if (generation === this.generation) this.detailLoading.set(false); }
  }

  private async loadResult(row: MapEntry, generation: number): Promise<void> {
    if (!canLoadPlanning(row) || this.resultCache[row.analysisId]) return;
    const existing = this.pending.get(row.analysisId);
    if (existing) return existing;
    this.resultRequests.update(count => count + 1);
    const request = (async () => {
      try {
        const plans = await this.api.getAnalysisPlannings(this.organizationId(), row.analysisId);
        if (generation === this.generation) this.resultCache[row.analysisId] = { plan: primaryPlanning(row, plans) };
      } catch {
        if (generation === this.generation) this.resultCache[row.analysisId] = { plan: null, error: true };
      } finally {
        if (generation === this.generation) {
          this.resultProgress.set(Object.keys(this.resultCache).length);
          this.pending.delete(row.analysisId); this.resultRequests.update(count => count - 1);
          if (!this.resultsLoading()) { this.results.set({ ...this.resultCache }); this.draw(); }
        }
      }
    })();
    this.pending.set(row.analysisId, request);
    return request;
  }

  private roofResult(rows: MapEntry[]): { row: MapEntry; plan: AnalysisPlanning } | null {
    const latestByAddress = new Map<string, { row: MapEntry; plan: AnalysisPlanning }>();
    const available = rows.flatMap(row => {
      const plan = this.results()[row.analysisId]?.plan;
      return plan ? [{ row, plan }] : [];
    });
    for (const item of available.sort((a, b) => new Date(b.row.createdAt ?? 0).getTime() - new Date(a.row.createdAt ?? 0).getTime() || a.row.analysisId.localeCompare(b.row.analysisId))) {
      if (!latestByAddress.has(addressKey(item.row))) latestByAddress.set(addressKey(item.row), item);
    }
    return latestByAddress.size === 1 ? [...latestByAddress.values()][0] : null;
  }

  private draw(): void {
    const map = this.map;
    if (!map) return;
    const bounds = map.getBounds();
    const paint = this.paint();
    const painted = (rows: MapEntry[]) => (paint ? rows.map(paint).find(Boolean) ?? null : null);
    const visible = this.located().filter(row => bounds.contains(location(row)!));
    const wanted = new Set<string>();
    const matched = new Set<string>();
    if (map.getZoom() >= ROOF_ZOOM) for (const building of this.buildings.values()) {
      if (!bounds.intersects(L.latLngBounds(building.ring))) continue;
      const occupants = this.buildingAnalyses.get(building.id) ?? [];
      if (!occupants.length) continue;
      const result = this.roofResult(occupants);
      // Keep address markers when several results share one outline.
      if (paint || result || !occupants.some(row => this.results()[row.analysisId]?.plan)) {
        for (const row of occupants) matched.add(row.analysisId);
      }
      const row = result?.row ?? occupants.find(item => item.analysisId === this.selected()?.analysisId) ?? occupants[0];
      const selected = occupants.some(item => item.analysisId === this.selected()?.analysisId);
      const key = `roof:${building.id}`;
      wanted.add(key);
      let layer = this.rendered.get(key);
      if (!layer) {
        layer = L.polygon(building.ring, { smoothFactor: 0 }).addTo(this.layers);
        this.rendered.set(key, layer);
      }
      const color = painted(occupants);
      layer.setStyle({
        color: selected ? '#18234b' : color ?? (result ? capacityColor(result.plan.plantCapacity) : '#ffffff'),
        weight: selected ? 2 : 1,
        fillColor: color ?? capacityColor(result?.plan.plantCapacity), fillOpacity: color || result ? 0.9 : 0.45,
      });
      layer.off('click').on('click', (event: L.LeafletMouseEvent) => this.open(row, true, occupants, event.latlng));
      this.accessible(layer, row);
    }
    const resultMarkers: L.CircleMarker[] = [];
    const drawnAddresses = new Set<string>();
    for (const candidate of visible) {
      if (matched.has(candidate.analysisId)) continue;
      const keyForAddress = addressKey(candidate);
      if (drawnAddresses.has(keyForAddress)) continue;
      drawnAddresses.add(keyForAddress);
      const group = this.addressGroups().get(keyForAddress) ?? [candidate];
      const row = this.roofResult(group)?.row ?? group[0];
      const plan = this.results()[row.analysisId]?.plan;
      const selected = row.analysisId === this.selected()?.analysisId;
      const key = `address:${keyForAddress}`;
      wanted.add(key);
      let layer = this.rendered.get(key) as L.CircleMarker | undefined;
      if (!layer) {
        layer = L.circleMarker(location(row)!).addTo(this.layers);
        this.rendered.set(key, layer);
        this.accessible(layer, row);
      }
      layer.off('click').on('click', () => {
        const occupants = [...this.buildingAnalyses.values()].find(rows => rows.some(item => item.analysisId === row.analysisId));
        this.open(row, !!occupants, occupants ?? group);
      });
      const color = painted(group);
      const near = map.getZoom() >= ROOF_ZOOM;
      if (paint) {
        layer.setRadius(selected ? 6 : color ? (near ? 6 : 3.5) : near ? 4 : 2);
        layer.setStyle({
          weight: selected ? 1.5 : 0.5, color: selected ? '#18234b' : '#ffffff',
          fillColor: color ?? UNKNOWN_COLOR, fillOpacity: color || selected ? 1 : 0.35,
        });
      } else {
        layer.setRadius(plan || selected ? 6 : near ? 4 : 2.5);
        layer.setStyle({
          weight: plan || selected ? 1.5 : 0.5, color: selected ? '#18234b' : '#ffffff',
          fillColor: capacityColor(plan?.plantCapacity), fillOpacity: plan || selected ? 1 : 0.65,
        });
      }
      if (color || plan || selected) resultMarkers.push(layer);
    }
    for (const [key, layer] of this.rendered) {
      if (!wanted.has(key)) { this.layers.removeLayer(layer); this.rendered.delete(key); }
    }
    // Available results must remain visible above the many unplanned addresses.
    for (const layer of resultMarkers) layer.bringToFront();
  }

  private accessible(layer: L.Path, row: MapEntry): void {
    const element = layer.getElement();
    layer.bindTooltip(() => { const label = document.createElement('span'); label.textContent = this.label(row); return label; });
    if (!element) return;
    element.setAttribute('role', 'button'); element.setAttribute('tabindex', '-1');
    element.setAttribute('aria-label', this.label(row));
    // Address search provides keyboard access alongside canvas rendering.
  }

  ngOnDestroy(): void {
    this.generation++; this.viewportVersion++; clearTimeout(this.timer);
    this.controller?.abort(); this.observer?.disconnect(); this.layers.clearLayers(); this.rendered.clear();
    const map = this.map;
    this.map = undefined;
    map?.stop(); map?.off();
    // Leaflet ends a zoom on a timer that map.stop() does not cancel; removing the map first makes that timer throw.
    setTimeout(() => map?.remove(), LEAFLET_ZOOM_END_MS);
  }
}
